//core
import React, {useEffect, useState} from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Chip,
  Divider,
  Tooltip,
} from "@mui/material";

//colors
import { amber, green } from '@mui/material/colors';

//styles
import { useStyles } from "./styles";
import {
  ChipGrey,
  ChipLightGreen,
  ChipYellow
} from "../../styles";


//utils
import { getRevenueShareTabsList } from "../PaymentPlanForm/utils";

//hooks
import { useUser } from "../../../../../store/common/user/useUser";

//components
import ErrorData from "../../../../common/ErrorData";
import CircularLoader from "../../../../UI/CircularLoader";
import Cpa from "./components/Cpa";
import RevenueShare from "./components/RevenueShare";


interface IDealDetailsViewProps {
  constantsData: any;
  conditionData: any;
  conditionError: any;
  fullWidth?: boolean;
}

const DealDetailsView: React.FC<IDealDetailsViewProps> = ({
  constantsData,
  conditionData,
  conditionError,
  fullWidth = false,
}): JSX.Element => {
  const { t } = useTranslation();
  const styles = useStyles();

  const { user } = useUser();

  return (
    <Box className={`${styles.block} ${fullWidth ? 'full' : ''}`}>
      {!!constantsData && !!conditionData || !!conditionError ? (
        <>
          {!!constantsData && !!conditionData && (
            <Box className={styles.fieldsWrapper}>
              <Box className={styles.field}>
                <Typography variant="body2">
                  {t("common.forms.deals.type")}:
                </Typography>
                <ChipGrey
                  label={t(`common.components.type_of_transaction.${conditionData.condition.type_of_transaction}`)}
                  size="small"
                />
              </Box>

              {(conditionData.condition.type_of_transaction === 'revenue_share' || conditionData.condition.type_of_transaction === 'hybrid') && (<>
                <Box className={styles.field}>
                  <Typography variant="body2">
                    {t("admin.deals.summary.settings")}:
                  </Typography>
                  <ChipGrey
                    label={
                      conditionData.condition.accrual_of_interest_on_the_upper_interval
                        ? t(`common.components.accrual_of_interest_on_the_upper_interval.upper_interval`)
                        : t(`common.components.accrual_of_interest_on_the_upper_interval.each_interval`)
                    }
                    size="small"
                  />
                  <Typography variant="body2">
                    {t("admin.deals.summary.and")}
                  </Typography>
                  <ChipGrey
                    label={
                      conditionData.condition.transferring_a_negative_commission
                        ? t(`common.components.transferring_a_negative_commission.enable`)
                        : t(`common.components.transferring_a_negative_commission.disable`)
                    }
                    size="small"
                  />
                </Box>
              </>)}

              {!!conditionData.condition.condition_filters_attributes.length && (<>
                <Box className={styles.groupWrapper}>
                  {conditionData.condition.condition_filters_attributes.map((item: any, i: number) => {
                    return (
                      <Box className={`
                          ${styles.group} 
                          ${conditionData.condition.filter_based_on === "country_brand_and_currency" 
                            && item.name === "filters.filter_all_brands_and_currencies" ? 'last-filter' : ''}
                          ${conditionData.condition.condition_filters_attributes.length === 1 ? 'one' : ''}
                          ${conditionData.condition.filter_based_on === "by_brand" ? 'default-filter' : ''}
                        `}
                        key={item.id}
                      >
                        { conditionData.condition.filter_based_on === 'by_brand' ? (
                          <>
                            <Box className={styles.filterWrapper}>
                              <Box className={styles.field}>
                                <Typography variant="body2">
                                  {t("admin.deals.summary.for_brand")}:
                                </Typography>

                                {item.value.hasOwnProperty('brandsLabels') && !!item.value.brandsLabels.length ? item.value.brandsLabels.map((brand: any, i:number) => (
                                  <>
                                    {user.user.namespace === "affiliate" ? (
                                      <Tooltip key={`${brand?.id}-${i}`} title={brand?.label.replace(` - ${brand?.id}`, '')} placement="bottom">
                                        <ChipLightGreen
                                          key={`${brand?.id}-${i}-`}
                                          label={brand?.label.replace(` - ${brand?.id}`, '')}
                                          size="small"
                                          sx={{ maxWidth: '120px' }}
                                        />
                                      </Tooltip>
                                    ) : (
                                      <Tooltip key={`${brand?.id}-${i}`} title={brand?.label} placement="bottom">
                                        <ChipLightGreen
                                          key={`${brand?.id}-${i}-`}
                                          label={brand?.label}
                                          size="small"
                                          sx={{ maxWidth: '120px' }}
                                        />
                                      </Tooltip>
                                    )}
                                  </>

                                )) : (
                                  <ChipLightGreen
                                    label={t("admin.deals.form.filter.all_brands")}
                                    size="small"
                                  />
                                )}
                              </Box>
                              {item.value.hasOwnProperty('countries') && !!item.value.countries.length && (
                                <Box className={styles.field}>
                                  <Typography variant="body2">
                                    {t("admin.deals.form.filter.country")}:
                                  </Typography>
                                  {item.value.countries.map((country: string) => (
                                    <ChipYellow
                                      key={country}
                                      label={t(`common.components.creative.list_geolocales.${country}`)}
                                      size="small"
                                    />
                                  ))}
                                </Box>
                              )}
                              {item.value.hasOwnProperty('currencies') && !!item.value.currencies.length && (
                                <Box className={styles.field}>
                                  <Typography variant="body2">
                                    {t("admin.deals.form.filter.currency")}:
                                  </Typography>
                                  {item.value.currencies.map((currency: string) => (
                                    <ChipYellow
                                      key={currency}
                                      label={currency}
                                      size="small"
                                    />
                                  ))}
                                </Box>
                              )}
                            </Box>
                          </>
                        ) : (
                          <>
                            <Box key={item.id}>
                              <Box className={styles.field}>
                                <Typography variant="body2">
                                  {t("admin.deals.form.filter.brand_and_currency")}:
                                </Typography>

                                {item.name === "filters.filter_all_brands_and_currencies" ? (
                                  <ChipYellow
                                    label={t("admin.deals.form.filter.all_countries")}
                                    size="small"
                                  />
                                ) : (<>
                                  {!!item.value.length ? item.value.map((value: string) => (
                                    <ChipYellow
                                      key={value}
                                      label={value}
                                      size="small"
                                    />
                                  )) : (
                                    <ChipYellow
                                      label={t("admin.deals.form.filter.all_countries")}
                                      size="small"
                                    />
                                  )}
                                </>)}
                              </Box>
                            </Box>
                          </>
                        )}

                        {conditionData.condition.type_of_transaction === 'cpa' && (
                          <Cpa data={item} />
                        )}

                        {conditionData.condition.type_of_transaction === 'revenue_share' && <>
                          <RevenueShare
                            data={item.condition_steps_attributes}
                            tabs={getRevenueShareTabsList(constantsData.condition_steps.obtained_result, conditionData.condition.type_of_transaction)}
                            type={conditionData.condition.type_of_transaction}
                          />
                        </>}

                        {conditionData.condition.type_of_transaction === 'hybrid' && <>
                          <Cpa data={item} />
                          <RevenueShare
                            data={item.condition_steps_attributes}
                            tabs={getRevenueShareTabsList(constantsData.condition_steps.obtained_result, conditionData.condition.type_of_transaction)}
                            type={conditionData.condition.type_of_transaction}
                          />
                        </>}

                        <Divider orientation="horizontal" />
                      </Box>
                    )
                  })}
                </Box>
              </>)}
            </Box>
          )}

          {!!conditionError && (
            <ErrorData
              title={conditionError.status}
              text={conditionError.statusText}
              elevation={0}
            />
          )}
        </>
      ) : (
        <CircularLoader size={32} />
      )}
    </Box>
  );
};

export default DealDetailsView;
